import { useMemo } from 'react';

import { Badge, Divider, FlexAligner, Typography } from '@scaleaq/scaleui';
import { CameraInfo, InventoryType, WinchCalibrationState } from '@scaleaq/sitecontrol-lib';
import { HouseCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AnchorBanIcon } from '@/icons';
import { colors, responsive } from '@/utils';

import {
  CameraStreamAlertMenu,
  type CameraStreamAlert,
} from './cameraStreamWinchStatus/CameraStreamAlertMenu.tsx';
import { StyledHeaderDividerFade } from './cameraStreamWinchStatus/cameraStreamWinchStatus.styles.ts';
import { useWinchCalibrationBadges } from './cameraStreamWinchStatus/useWinchCalibrationBadges.ts';
import { useCameraChanged } from './useCameraChanged.ts';

type CameraStreamWinchStatusProps = {
  camera: CameraInfo;
  /** False in fullscreen so the alert menu renders inside the fullscreen element. */
  usePortal: boolean;
};

/** Winch maintenance and calibration status, listed in the alert menu behind the bell. */
export const CameraStreamWinchStatus = ({ camera, usePortal }: CameraStreamWinchStatusProps) => {
  const { t } = useTranslation('cameraControl');
  // The `camera` prop is a one-off REST fetch; the live winch info arrives in `CameraChanged`.
  const liveCamera = useCameraChanged(camera.id);
  const winchInfo = liveCamera?.winchInfo ?? camera.winchInfo;
  const { isInMaintenanceMode, winchType, calibration } = winchInfo ?? {};
  const badges = useWinchCalibrationBadges(calibration);
  const isCalibrating =
    winchType === InventoryType.SmartWinch && calibration?.state === WinchCalibrationState.Running;

  const alerts = useMemo(() => {
    const list: CameraStreamAlert[] = [];
    if (isInMaintenanceMode) {
      list.push({
        key: 'maintenance',
        content: (
          <>
            <HouseCog size={20} style={{ flexShrink: 0 }} />
            <Typography type='body_medium'>{t('winchMaintenanceMode')}</Typography>
          </>
        ),
      });
    }
    if (isCalibrating || badges.length > 0) {
      list.push({
        key: 'calibration',
        content: (
          <>
            {isCalibrating && <AnchorBanIcon width={20} height={20} style={{ flexShrink: 0 }} />}
            <FlexAligner direction='column' alignment='top-left' gap={responsive.spacing.xsmall}>
              {isCalibrating && <Typography type='body_medium'>{t('winchCalibrating')}</Typography>}
              <FlexAligner alignment='center-left' gap={responsive.spacing.xsmall}>
                <Typography type='body_medium'>{t('winchCalibrationStatus')}</Typography>
                {badges.map(({ key, type, label }) => (
                  <Badge key={key} size='medium' type={type} outline label={label} />
                ))}
              </FlexAligner>
            </FlexAligner>
          </>
        ),
      });
    }
    return list;
  }, [isInMaintenanceMode, isCalibrating, badges, t]);

  if (alerts.length === 0) return null;

  return (
    <FlexAligner gap={responsive.spacing.large} fullWidth={false} alignment='center-right'>
      <CameraStreamAlertMenu
        alerts={alerts}
        label={t('alerts')}
        bellLabel={t('alertsWithCount', { count: alerts.length })}
        // Maintenance or a running calibration means the winch is not operational.
        color={
          isInMaintenanceMode || isCalibrating
            ? colors.palette.red[400]
            : colors.palette.orange[400]
        }
        usePortal={usePortal}
      />
      <StyledHeaderDividerFade>
        <Divider
          orientation='vertical'
          // Vertical Divider length is `width`; match the body_large line height of the sensor-reading dividers.
          width={responsive.font.line.height.large}
          color={colors.stroke.neutral.solid[100]}
        />
      </StyledHeaderDividerFade>
    </FlexAligner>
  );
};
